export const REPORT_PROGRESS_THEME = {
  primary: "#DC4563",
  secondary: "#AC1FAE",
  purple: "#9333ea",
  ink: "#0f172a",
  muted: "#6b7280",
  surface: "#ffffff",
  bg: "#f8fafc",
  border: "#e5e7eb",
}

function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

export function formatReportProgressMessage(progress) {
  if (!progress) return "Generating Report…"
  const { phase, label, message, pagesCurrent, pagesTotal } = progress
  if (phase === "finalize") return "Finalizing your report…"
  if (phase === "pdf") {
    if (pagesTotal > 0 && pagesCurrent != null) {
      return `Assembling report pages (${pagesCurrent}/${pagesTotal})`
    }
    return message || "Assembling report pages…"
  }
  if (label) return `Current: ${label}`
  return message || "Generating Report…"
}

export function buildReportProgressHtml(progress) {
  const pct = Math.max(
    0,
    Math.min(100, Math.round(progress?.percent ?? 0)),
  )
  const title = progress?.phase === "capture" ? "Capturing dashboard charts" : "Building your report"
  const detail = formatReportProgressMessage(progress)
  const chartPhases = progress?.phase === "capture" || progress?.phase === "data"
  const pendingCount = chartPhases
    ? progress?.pendingCharts?.length ??
      (progress?.chartsRemaining != null
        ? progress.chartsRemaining
        : progress?.total > 0
          ? Math.max(0, progress.total - (progress.chartsAdded ?? progress.current ?? 0))
          : 0)
    : 0
  const pending =
    chartPhases && pendingCount > 0
      ? `${pendingCount} chart${pendingCount === 1 ? "" : "s"} pending`
      : ""
  const completedList =
    progress?.completedCharts?.length > 0
      ? progress.completedCharts.slice(-3).join(", ")
      : ""

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Generating report…</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif;
      background: ${REPORT_PROGRESS_THEME.bg};
      color: ${REPORT_PROGRESS_THEME.ink};
    }
    .card {
      width: min(420px, 100%);
      background: ${REPORT_PROGRESS_THEME.surface};
      border: 1px solid ${REPORT_PROGRESS_THEME.border};
      border-radius: 16px;
      padding: 28px 24px 24px;
      box-shadow: 0 12px 40px rgba(15, 23, 42, 0.08);
      text-align: center;
    }
    .brand {
      font-size: 0.95rem;
      font-weight: 800;
      letter-spacing: -0.02em;
      background: linear-gradient(90deg, ${REPORT_PROGRESS_THEME.primary}, ${REPORT_PROGRESS_THEME.secondary});
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      margin-bottom: 16px;
    }
    h1 {
      margin: 0 0 8px;
      font-size: 1.1rem;
      font-weight: 700;
    }
    .detail {
      margin: 0 0 6px;
      font-size: 0.875rem;
      color: ${REPORT_PROGRESS_THEME.muted};
      line-height: 1.45;
    }
    .pending {
      margin: 0 0 18px;
      font-size: 0.75rem;
      color: ${REPORT_PROGRESS_THEME.purple};
      font-weight: 600;
    }
    .track {
      height: 8px;
      border-radius: 999px;
      background: #f3f4f6;
      overflow: hidden;
    }
    .bar {
      height: 100%;
      width: ${pct}%;
      border-radius: 999px;
      background: linear-gradient(90deg, ${REPORT_PROGRESS_THEME.primary}, ${REPORT_PROGRESS_THEME.purple});
      transition: width 0.25s ease;
    }
    .pct {
      margin-top: 10px;
      font-size: 0.75rem;
      color: ${REPORT_PROGRESS_THEME.muted};
    }
  </style>
</head>
<body>
  <div class="card">
    <div class="brand">SoHo Social</div>
    <h1>${escapeHtml(title)}</h1>
    <p class="detail">${escapeHtml(detail)}</p>
    ${pending ? `<p class="pending">${escapeHtml(pending)}</p>` : ""}
    ${completedList ? `<p class="detail" style="margin-top:4px;font-size:0.72rem">Added: ${escapeHtml(completedList)}</p>` : ""}
    <div class="track"><div class="bar"></div></div>
    <div class="pct">${pct}% complete</div>
  </div>
</body>
</html>`
}

export function updateReportProgressPreviewTab(tab, progress) {
  if (!tab || tab.closed) return
  try {
    tab.document.open()
    tab.document.write(buildReportProgressHtml(progress))
    tab.document.close()
  } catch {
    // ignore cross-tab errors
  }
}
